import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { useNavigate } from 'react-router-dom';
import Lenis from 'lenis';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SearchWidget from '../components/SearchWidget';
import PropertyCard from '../components/PropertyCard';
import { propertiesAPI } from '../lib/api';
import { t } from '../lib/i18n';
import {
  ArrowRight, Home, TreePine, Building2, Crown, Utensils,
  Star, CheckCircle, Users, MapPin, Sparkles, ChevronRight
} from 'lucide-react';

const LOGO = "https://static.prod-images.emergentagent.com/jobs/6554b71a-e92e-45d6-a2ea-28b8d837c5dd/images/05ed22a83a162dbd4a68bdfa54453a7a8a9f16d8e3a633061831e14ecf8e4761.jpeg";

const MARQUEE_ITEMS = [
  'HYDERABAD', 'VIJAYAWADA', 'VISAKHAPATNAM', 'TIRUPATI', 'WARANGAL',
  'KURNOOL', 'KAKINADA', 'NELLORE', 'RAJAHMUNDRY', 'GUNTUR', 'ONGOLE',
  'ANANTAPUR', 'CHITTOOR', 'ELURU', 'NIZAMABAD'
];

const PRICING = [
  { type: 'Homestay (1-2 Rooms)', price: 799, icon: <Home size={20} /> },
  { type: 'Homestay (3+ Rooms)', price: 1999, icon: <Home size={20} /> },
  { type: 'PG / Hostel', price: 999, icon: <Building2 size={20} /> },
  { type: 'Hotel', price: 1499, icon: <Building2 size={20} /> },
  { type: 'Farmhouse', price: 1499, icon: <TreePine size={20} /> },
  { type: 'Penthouse', price: 1499, icon: <Crown size={20} /> },
  { type: 'Function / Banquet Hall', price: 1999, icon: <Sparkles size={20} /> },
];

// ---- Scroll reveal wrapper ----
const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px 0px' });
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

// ---- Hero Section ----
const HeroSection = () => {
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroLines = [t('hero.line1'), t('hero.line2'), t('hero.line3'), t('hero.line4')];
  const lineVariant = {
    hidden: { y: '110%' },
    visible: (i) => ({
      y: 0,
      transition: { delay: 0.3 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] }
    })
  };
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-navy-dark">
      <motion.div className="absolute inset-0 scale-110" style={{ y: imageY }}>
        <img
          src="https://images.unsplash.com/photo-1607567618395-62fc2d132c3e?crop=entropy&cs=srgb&fm=jpg&w=1800&q=85"
          alt="Luxury villa with pool"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#0A1628]/70 to-[#0A1628]/20" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 w-full pb-24 pt-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Overline */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="h-px w-8 bg-gold" />
            <span className="text-gold text-xs uppercase tracking-[0.3em] font-medium">{t('hero.overline')}</span>
          </motion.div>

          {/* Kinetic heading */}
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-black text-white leading-[1.02] tracking-tighter mb-10">
            {heroLines.map((line, i) => (
              <span key={i} className="line-reveal block">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          {/* Sub text */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.6 }}
            className="text-white/65 text-base lg:text-lg max-w-lg mb-12 leading-relaxed"
          >
            {t('hero.description')}
          </motion.p>

          {/* Search widget */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <SearchWidget />
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 right-14 hidden lg:flex flex-col items-center gap-3"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
      >
        <span className="text-white/30 text-[10px] uppercase tracking-[0.3em]" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
        <motion.div
          animate={{ scaleY: [1, 0.3, 1] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
          className="w-px h-16 bg-gradient-to-b from-white/30 to-transparent origin-top"
        />
      </motion.div>
    </section>
  );
};

// ---- Destinations Marquee ----
const DestinationsMarquee = () => (
  <div data-testid="marquee-section" className="py-10 border-y border-gray-100 bg-[#FAFAF9] overflow-hidden marquee-container">
    <Marquee speed={30} gradient={false}>
      {MARQUEE_ITEMS.map((item, i) => (
        <span key={i} className="font-serif text-5xl lg:text-7xl text-navy/10 font-black mx-10 tracking-tighter select-none">
          {item}
        </span>
      ))}
    </Marquee>
  </div>
);

// ---- Experiences Section ----
const ExperiencesSection = () => {
  const navigate = useNavigate();
  return (
    <section data-testid="experiences-section" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="mb-20">
          <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium">What we offer</span>
          <h2 className="font-serif text-4xl lg:text-6xl font-black text-navy mt-3 tracking-tight">
            Two ways to experience <br className="hidden lg:block" />the region.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stay */}
          <Reveal delay={0.1}>
            <div
              data-testid="experience-stay"
              onClick={() => navigate('/search?experience=stay')}
              className="group relative rounded-3xl overflow-hidden cursor-pointer h-[520px]"
            >
              <img
                src="https://images.unsplash.com/photo-1750420556288-d0e32a6f517b?w=900&q=85"
                alt="Stay"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-10">
                <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium block mb-3">{t('exp.stay.overline')}</span>
                <h3 className="font-serif text-3xl lg:text-4xl font-black text-white tracking-tight mb-3">
                  {t('exp.stay.title')}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-xs">{t('exp.stay.desc')}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Homestays', 'Hotels', 'PGs', 'Hostels', 'Farmhouses'].map(item => (
                    <span key={item} className="bg-white/15 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full border border-white/20">
                      {item}
                    </span>
                  ))}
                </div>
                <button className="flex items-center gap-2 text-white text-sm font-medium group-hover:text-gold transition-colors duration-200">
                  Explore Stays <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Reveal>

          {/* Celebrate */}
          <Reveal delay={0.2}>
            <div
              data-testid="experience-celebrate"
              onClick={() => navigate('/search?experience=celebrate')}
              className="group relative rounded-3xl overflow-hidden cursor-pointer h-[520px]"
            >
              <img
                src="https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?w=900&q=85"
                alt="Celebrate"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-10">
                <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium block mb-3">{t('exp.celebrate.overline')}</span>
                <h3 className="font-serif text-3xl lg:text-4xl font-black text-white tracking-tight mb-3">
                  {t('exp.celebrate.title')}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-xs">{t('exp.celebrate.desc')}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Function Halls', 'Banquet Halls', 'Farmhouses', 'Penthouses'].map(item => (
                    <span key={item} className="bg-white/15 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full border border-white/20">
                      {item}
                    </span>
                  ))}
                </div>
                <button className="flex items-center gap-2 text-white text-sm font-medium group-hover:text-gold transition-colors duration-200">
                  Explore Venues <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ---- Featured Properties ----
const FeaturedSection = ({ properties }) => {
  const navigate = useNavigate();
  const featured = properties.slice(0, 6);

  return (
    <section data-testid="featured-section" className="py-32 bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="flex items-end justify-between mb-14">
          <div>
            <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium block mb-3">Featured</span>
            <h2 className="font-serif text-4xl lg:text-5xl font-black text-navy tracking-tight">
              Handpicked for you
            </h2>
          </div>
          <button
            onClick={() => navigate('/search')}
            className="hidden sm:flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors duration-200"
          >
            View all <ChevronRight size={16} />
          </button>
        </Reveal>

        {featured.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((property, i) => (
              <PropertyCard key={property.id} property={property} index={i} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-gray-400">
            <p>Loading featured properties...</p>
          </div>
        )}

        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/search')}
            className="border border-navy text-navy hover:bg-navy hover:text-white px-8 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200"
          >
            View All Properties
          </button>
        </div>
      </div>
    </section>
  );
};

// ---- Manifesto Section ----
const ManifestoSection = () => {
  const CHAPTERS = [
    {
      num: '01',
      title: 'Regional Roots',
      desc: 'Built for Andhra Pradesh and Telangana first. Every property is reviewed against regional hospitality standards, and every listing is verified for authenticity.'
    },
    {
      num: '02',
      title: 'Verified Quality',
      desc: 'No unverified listings. Every partner goes through an approval process. Every property is checked before it appears on HYGAD.'
    },
    {
      num: '03',
      title: 'Seamless Discovery',
      desc: 'From intimate farmhouse retreats to grand banquet halls — one platform, two experiences, thousands of possibilities across two states.'
    },
    {
      num: '04',
      title: 'Partner First',
      desc: 'Property owners are our partners, not just listings. Partners get priority placement, dedicated support, and a verified trust badge once onboarded.'
    }
  ];

  return (
    <section data-testid="manifesto-section" className="py-32 bg-navy noise-overlay relative">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal className="mb-20">
          <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium block mb-4">Our Philosophy</span>
          <h2 className="font-serif text-4xl lg:text-6xl font-black text-white tracking-tighter max-w-2xl leading-tight">
            Why HYGAD exists.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.num} delay={i * 0.08}>
              <div className="bg-navy p-10 hover:bg-navy-light transition-colors duration-300 group">
                <div className="font-serif text-6xl font-black text-white/10 group-hover:text-gold/20 transition-colors duration-300 mb-6 leading-none">
                  {ch.num}
                </div>
                <div className="h-px w-10 bg-gold mb-6" />
                <h3 className="font-serif text-2xl font-bold text-white mb-4">{ch.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{ch.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---- Stats Section ----
const StatsSection = () => {
  const STATS = [
    { value: '2', label: 'States' },
    { value: '10+', label: 'Cities' },
    { value: '200+', label: 'Properties' },
    { value: '5000+', label: 'Happy Guests' },
  ];
  return (
    <section data-testid="stats-section" className="py-24 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 0.1} className="text-center">
              <div className="font-serif text-5xl lg:text-6xl font-black text-navy mb-2">{stat.value}</div>
              <div className="text-sm text-gray-400 uppercase tracking-[0.15em]">{stat.label}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---- Partner CTA / Pricing Section ----
const PartnerPricingSection = () => {
  const navigate = useNavigate();
  return (
    <section data-testid="pricing-section" id="pricing" className="py-32 bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Copy */}
          <Reveal>
            <span className="text-gold text-xs uppercase tracking-[0.25em] font-medium block mb-4">
              {t('partner.cta.overline')}
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl font-black text-navy tracking-tight mb-6 leading-tight">
              {t('partner.cta.title')}
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-8 max-w-md">
              {t('partner.cta.desc')}
            </p>
            <div className="space-y-3 mb-10">
              {[
                'One-Time Partner Onboarding Fee',
                'Business Verification & Trust Badge',
                'Listed on HYGAD marketplace',
                'Direct booking enquiry notifications',
                'Partner dashboard & analytics',
                'Priority support during launch'
              ].map(feat => (
                <div key={feat} className="flex items-center gap-3">
                  <CheckCircle size={16} className="text-gold shrink-0" />
                  <span className="text-sm text-gray-600">{feat}</span>
                </div>
              ))}
            </div>
            <button
              data-testid="become-partner-btn"
              onClick={() => navigate('/auth?tab=partner')}
              className="bg-navy hover:bg-navy-light text-white px-8 py-4 rounded-xl font-semibold text-sm transition-colors duration-200 flex items-center gap-2 border border-gold/20"
            >
              {t('partner.cta.button')} <ArrowRight size={16} />
            </button>
          </Reveal>

          {/* Right: Pricing cards */}
          <Reveal delay={0.15}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PRICING.map((tier, i) => (
                <motion.div
                  key={tier.type}
                  data-testid={`pricing-card-${i}`}
                  whileHover={{ scale: 1.02, transition: { duration: 0.15 } }}
                  className="bg-white border border-gray-100 rounded-2xl p-5 flex items-center gap-4 hover:border-gold/40 transition-colors duration-200"
                  style={{ boxShadow: '0 1px 4px rgba(10,22,40,0.06)' }}
                >
                  <div className="w-10 h-10 bg-gold/10 rounded-xl flex items-center justify-center text-gold shrink-0">
                    {tier.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-navy leading-snug truncate">{tier.type}</div>
                    <div className="text-gold font-bold text-lg">₹{tier.price.toLocaleString('en-IN')}</div>
                    <div className="text-gray-400 text-[11px]">one-time onboarding fee</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ---- Main Landing ----
const Landing = () => {
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    // Init Lenis smooth scroll
    const lenis = new Lenis({ duration: 1.2, easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    const rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  useEffect(() => {
    propertiesAPI.search({ limit: 6 }).then(data => {
      setProperties(data.properties || []);
    }).catch(console.error);
  }, []);

  return (
    <div data-testid="landing-page">
      <Navbar transparent />
      <HeroSection />
      <DestinationsMarquee />
      <ExperiencesSection />
      <FeaturedSection properties={properties} />
      <ManifestoSection />
      <StatsSection />
      <PartnerPricingSection />
      <Footer />
    </div>
  );
};

export default Landing;
